import { useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { 
  Upload, FileText, Target, ArrowRight, Loader2, 
  CheckCircle, AlertCircle, TrendingUp,
  Shield, Sparkles, RefreshCw, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import { getAuthToken } from '../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const MatchScorePage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Upload, 2: Job Description, 3: Results
  const [file, setFile] = useState(null);
  const [resumeId, setResumeId] = useState(null);
  const [jobDescription, setJobDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [matchData, setMatchData] = useState(null);

  // Handle file upload
  const handleFileUpload = useCallback(async (e) => {
    const uploadedFile = e.target.files?.[0];
    if (!uploadedFile) return;

    const fileExt = uploadedFile.name.split('.').pop()?.toLowerCase();
    if (!['pdf', 'docx'].includes(fileExt)) {
      toast.error('Only PDF and DOCX files are supported');
      return;
    }

    setFile(uploadedFile);
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', uploadedFile);

      const token = getAuthToken();
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API}/resumes/upload`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: formData,
      });

      const data = await response.json();
      if (response.ok) {
        setResumeId(data.resume_id);
        toast.success('Resume uploaded successfully');
        setStep(2);
      } else {
        toast.error(data.detail || 'Upload failed');
        setFile(null);
      }
    } catch (error) {
      toast.error('Failed to upload resume');
      setFile(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Analyze job match
  const handleAnalyzeMatch = async () => {
    if (!resumeId || !jobDescription.trim()) {
      toast.error('Please paste a job description');
      return;
    }
    
    if (jobDescription.trim().length < 50) {
      toast.error('Job description is too short. Please paste the full job posting.');
      return;
    }
    
    setAnalyzing(true);
    
    try {
      const token = getAuthToken();
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API}/resumes/job-match-detailed`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ resume_id: resumeId, job_description: jobDescription }),
      });
      
      const data = await response.json();
      if (response.ok && data.success) {
        setMatchData(data.job_match);
        setStep(3);
        toast.success('Analysis complete!');
      } else {
        toast.error(data.detail || 'Failed to analyze job match');
      }
    } catch (error) {
      toast.error('Failed to analyze job match');
    } finally {
      setAnalyzing(false);
    }
  };

  // Score color helpers
  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-amber-500';
    return 'bg-red-500';
  };

  const getScoreRing = (score) => {
    if (score >= 80) return 'ring-green-500';
    if (score >= 60) return 'ring-amber-500';
    return 'ring-red-500';
  };

  // Handle "Get Optimized Resume" - redirect to template selection with resume data
  const handleGetOptimizedResume = () => {
    // Store resume info in session for the Convert flow
    // This allows ConvertPage to skip the upload step and go directly to template selection
    sessionStorage.setItem('matchScoreResumeId', resumeId);
    sessionStorage.setItem('fromMatchScore', 'true');
    sessionStorage.setItem('uploadedResumeId', resumeId); // For ConvertPage to recognize
    
    // Store the match data so we can apply AI improvements when converting
    if (matchData) {
      sessionStorage.setItem('matchScoreData', JSON.stringify({
        missing_keywords: matchData.missing_keywords || [],
        weak_points: matchData.weak_points || [],
        suggestions: matchData.suggestions || [],
        job_description: jobDescription
      }));
    }
    
    // Navigate to convert page - it will detect fromMatchScore and skip upload
    navigate('/convert', { 
      state: { 
        fromMatchScore: true, 
        resumeId,
        matchData: matchData ? {
          missing_keywords: matchData.missing_keywords,
          weak_points: matchData.weak_points,
          suggestions: matchData.suggestions
        } : null
      } 
    });
  };

  // Reset and start over
  const handleStartOver = () => {
    setStep(1);
    setFile(null);
    setResumeId(null);
    setJobDescription('');
    setMatchData(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-slate-50">
      <Helmet>
        <title>Free ATS Resume Score Checker | FixResume24</title>
        <meta name="description" content="Check how well your resume matches any job description. Get your free ATS compatibility score, see missing keywords, and get AI-powered suggestions to improve your resume." />
        <meta name="keywords" content="ATS resume checker, resume score, job match, resume keywords, ATS optimization" />
        <link rel="canonical" href="https://www.fixresume24.com/match-score" />
      </Helmet>

      <Header />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Section */}
        <div className="text-center mb-8">
          <Badge variant="outline" className="mb-4 text-primary border-primary">
            <Target className="h-3 w-3 mr-1" />
            Free ATS Score Checker
          </Badge>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-primary tracking-tight mb-3">
            Job Match Score
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            See how well your resume matches any job description. Get instant feedback on missing keywords and AI-powered suggestions.
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="mb-8">
          <div className="flex justify-between text-sm text-muted-foreground mb-2">
            <span>Step {step} of 3</span>
            <span>{step === 1 ? 'Upload Resume' : step === 2 ? 'Paste Job Description' : 'View Results'}</span>
          </div>
          <Progress value={(step / 3) * 100} className="h-2" />
        </div>

        {/* Step 1: Upload Resume */}
        {step === 1 && (
          <div className="animate-fade-in">
            <Card className="border-slate-100 shadow-lg">
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-xl font-heading flex items-center justify-center gap-2">
                  <Upload className="h-5 w-5 text-primary" />
                  Upload Your Resume
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  We&apos;ll analyze your resume against the job you&apos;re targeting
                </p>
              </CardHeader>
              <CardContent className="p-8">
                <label className="block cursor-pointer">
                  <input
                    type="file"
                    accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="match-score-file-upload"
                  />
                  <div className="border-2 border-dashed border-slate-200 rounded-xl p-12 text-center hover:border-primary hover:bg-slate-50 transition-all">
                    {loading ? (
                      <div className="flex flex-col items-center">
                        <Loader2 className="h-12 w-12 text-primary animate-spin mb-4" />
                        <p className="text-muted-foreground">Processing your resume...</p>
                      </div>
                    ) : (
                      <>
                        <Upload className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-lg font-medium text-primary mb-2">
                          Drop your resume here
                        </p>
                        <p className="text-sm text-muted-foreground">
                          or click to browse (PDF or DOCX)
                        </p>
                        <div className="flex items-center justify-center gap-2 mt-4 text-xs text-green-600">
                          <Shield className="h-3.5 w-3.5" />
                          <span>Your resume is processed securely and never shared.</span>
                        </div>
                      </>
                    )}
                  </div>
                </label>

                {/* Benefits */}
                <div className="grid grid-cols-3 gap-4 mt-8">
                  <div className="text-center">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-2">
                      <Target className="h-5 w-5 text-green-600" />
                    </div>
                    <p className="text-xs text-muted-foreground">Match Score</p>
                  </div>
                  <div className="text-center">
                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-2">
                      <AlertCircle className="h-5 w-5 text-red-600" />
                    </div>
                    <p className="text-xs text-muted-foreground">Missing Keywords</p>
                  </div>
                  <div className="text-center">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-2">
                      <Sparkles className="h-5 w-5 text-blue-600" />
                    </div>
                    <p className="text-xs text-muted-foreground">AI Suggestions</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 2: Job Description */}
        {step === 2 && (
          <div className="animate-fade-in">
            <Card className="border-slate-100 shadow-lg">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-xl font-heading flex items-center gap-2">
                      <FileText className="h-5 w-5 text-primary" />
                      Paste Job Description
                    </CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      Copy and paste the full job posting you want to apply for
                    </p>
                  </div>
                  {file && (
                    <Badge variant="outline" className="text-green-600 border-green-300">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      {file.name}
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <Textarea
                  value={jobDescription}
                  onChange={(e) => setJobDescription(e.target.value)}
                  placeholder="Paste the full job description here...

Example:
We are looking for a Senior Software Engineer with 5+ years of experience in React, Node.js, and cloud technologies. You will be responsible for designing and implementing scalable solutions..."
                  className="min-h-[300px] text-sm"
                  data-testid="job-description-textarea"
                />
                
                <div className="flex items-center justify-between mt-4">
                  <p className="text-xs text-muted-foreground">
                    {jobDescription.length} characters {jobDescription.length < 50 && '(minimum 50)'}
                  </p>
                  <div className="flex gap-3">
                    <Button 
                      variant="outline" 
                      onClick={() => setStep(1)}
                      data-testid="back-to-upload-btn"
                    >
                      Back
                    </Button>
                    <Button 
                      onClick={handleAnalyzeMatch}
                      disabled={analyzing || jobDescription.trim().length < 50}
                      data-testid="analyze-match-btn"
                    >
                      {analyzing ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Analyzing...</>
                      ) : (
                        <><Target className="mr-2 h-4 w-4" /> Analyze Match</>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 3: Results */}
        {step === 3 && matchData && (
          <div className="animate-fade-in space-y-6">
            {/* Score Card */}
            <Card className="border-slate-100 shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-slate-900 to-slate-800 p-8 text-white">
                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                  {/* Circular Gauge */}
                  <div className="flex items-center gap-6">
                    <div className="relative">
                      <svg className="w-32 h-32 transform -rotate-90">
                        <circle
                          cx="64"
                          cy="64"
                          r="56"
                          stroke="currentColor"
                          strokeWidth="12"
                          fill="none"
                          className="text-slate-700"
                        />
                        <circle
                          cx="64"
                          cy="64"
                          r="56"
                          stroke="currentColor"
                          strokeWidth="12"
                          fill="none"
                          strokeLinecap="round"
                          strokeDasharray={`${matchData.match_score * 3.52} 352`}
                          className={matchData.match_score >= 80 ? 'text-green-500' : matchData.match_score >= 60 ? 'text-amber-500' : 'text-red-500'}
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-4xl font-bold">{matchData.match_score}%</span>
                      </div>
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold">{matchData.verdict}</h2>
                      <p className="text-slate-300 text-sm mt-1">
                        Your resume match score for this position
                      </p>
                    </div>
                  </div>
                  
                  {/* Quick Stats */}
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div className="bg-white/10 rounded-lg p-3">
                      <p className="text-2xl font-bold text-green-400">{matchData.matching_keywords?.length || 0}</p>
                      <p className="text-xs text-slate-300">Keywords Found</p>
                    </div>
                    <div className="bg-white/10 rounded-lg p-3">
                      <p className="text-2xl font-bold text-red-400">{matchData.missing_keywords?.length || 0}</p>
                      <p className="text-xs text-slate-300">Keywords Missing</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Score Breakdown */}
              {matchData.score_breakdown && (
                <CardContent className="p-6">
                  <h3 className="font-semibold text-primary mb-4">Score Breakdown</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(matchData.score_breakdown).map(([key, value]) => (
                      <div key={key} className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground capitalize">{key.replace('_', ' ')}</span>
                          <span className="font-medium">{value}%</span>
                        </div>
                        <Progress value={value} className="h-2" />
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Strengths - FREE */}
            {matchData.strengths && matchData.strengths.length > 0 && (
              <Card className="border-green-200 bg-green-50/50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2 text-green-700">
                    <CheckCircle className="h-5 w-5" />
                    Your Strengths
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {matchData.strengths.map((strength, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-green-800">
                        <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-green-600" />
                        {strength}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Matching Keywords - FREE */}
            {matchData.matching_keywords && matchData.matching_keywords.length > 0 && (
              <Card className="border-slate-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    Keywords Found in Your Resume
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {matchData.matching_keywords.map((kw, i) => (
                      <Badge key={i} className="bg-green-100 text-green-700 hover:bg-green-200">
                        {kw}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Missing Keywords - FREE */}
            {matchData.missing_keywords && matchData.missing_keywords.length > 0 && (
              <Card className="border-red-200 bg-red-50/50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2 text-red-700">
                    <AlertCircle className="h-5 w-5" />
                    Missing Keywords (Add to Resume)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {matchData.missing_keywords.map((kw, i) => (
                      <Badge key={i} className="bg-red-100 text-red-700 hover:bg-red-200">
                        {kw}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-xs text-red-600 mt-3">
                    Add these keywords to improve your ATS score
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Weak Points with AI Rewrites - Now visible to all */}
            {matchData.weak_points && matchData.weak_points.length > 0 && (
              <Card className="border-amber-200 bg-amber-50/50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2 text-amber-700">
                    <TrendingUp className="h-5 w-5" />
                    Areas to Improve
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {matchData.weak_points.map((wp, i) => (
                      <div key={i} className="p-3 bg-white rounded-lg border border-amber-100">
                        <div className="flex items-start gap-2 mb-2">
                          <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-amber-800 font-medium">{wp.issue || 'Could be improved'}</p>
                        </div>
                        {wp.original && (
                          <p className="text-sm text-slate-600 pl-6 mb-2">
                            <span className="text-slate-400">Current:</span> {wp.original}
                          </p>
                        )}
                        {wp.rewrite && (
                          <p className="text-sm text-green-700 pl-6 bg-green-50 p-2 rounded">
                            <span className="text-green-600 font-medium">Suggested:</span> {wp.rewrite}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-amber-600 mt-3">
                    Click &quot;Get Optimized Resume&quot; below to automatically apply these improvements
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Suggestions - FREE */}
            {matchData.suggestions && matchData.suggestions.length > 0 && (
              <Card className="border-blue-200 bg-blue-50/50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2 text-blue-700">
                    <Sparkles className="h-5 w-5" />
                    Quick Improvement Tips
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {matchData.suggestions.map((sug, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-blue-800">
                        <ArrowRight className="h-4 w-4 mt-0.5 flex-shrink-0 text-blue-600" />
                        {sug}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Get Optimized Resume CTA Card */}
            <Card className="border-primary/30 bg-gradient-to-r from-primary/5 to-accent/5">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                      <Sparkles className="h-7 w-7 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-primary">Ready to Optimize Your Resume?</h3>
                      <p className="text-sm text-muted-foreground">
                        We&apos;ll automatically add missing keywords and improve weak sections
                      </p>
                    </div>
                  </div>
                  <Button onClick={handleGetOptimizedResume} size="lg" className="gap-2" data-testid="get-optimized-resume-btn">
                    <Zap className="h-4 w-4" />
                    Get Optimized Resume
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex flex-wrap justify-center gap-4">
              <Button variant="outline" onClick={handleStartOver} data-testid="start-over-btn">
                <RefreshCw className="mr-2 h-4 w-4" />
                Analyze Another Job
              </Button>
              <Button variant="outline" onClick={() => setStep(2)} data-testid="change-job-btn">
                <FileText className="mr-2 h-4 w-4" />
                Try Different Job
              </Button>
            </div>
          </div>
        )}
      </main>

      {/* Footer CTA */}
      <footer className="py-8 border-t border-slate-100 bg-white mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm text-muted-foreground mb-4">
            Want to create a new resume from scratch?
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/create-resume">
              <Button variant="outline" size="sm">Create Resume</Button>
            </Link>
            <Link to="/convert">
              <Button variant="outline" size="sm">Convert Existing Resume</Button>
            </Link>
            <Link to="/refine">
              <Button variant="outline" size="sm">Refine Resume</Button>
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MatchScorePage;
